import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import Button from 'components/atoms/Button';
import { Chips } from 'components/atoms/Chips';
import { Loader } from 'components/atoms/Loader';
import { CountdownTimer } from 'components/atoms/Timer/CountdownTimer';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { FormModal } from 'components/molecules/Modal';
import { Tab, TabMenu } from 'components/molecules/TabMenu';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';
import { ECaseTaskNames } from 'constants/CaseTaskNames';
import { ELegalEntityTypes, ELegalEntityTypesName, LETypes } from 'constants/LegalEntityTypes';
import { findInDictionary } from 'helpers/dictionary';
import { usePermission } from 'hooks/usePermission';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { useAlert } from 'models/alertContext';
import { useAuth } from 'models/authContext';
import {
  CaseStatus,
  FINAL_CASE_STATUSES,
  REPRINT_LICENCE_CASE_STATUSES,
  REVIEW_APPLICATION_CASE_STATUSES,
} from 'models/cases';
import { useDictionary } from 'models/dictionaryContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useLang } from 'models/langContext';
import { EPermission } from 'models/permissions';
import { ERouteLinks } from 'models/route';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';
import { EPaymentStatusId } from 'pages/LegalEntitiesPage/helpers';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import {
  getMissingFieldsForIndividual,
  getMissingFieldsForOrganisation,
} from 'pages/LegalEntitiesPage/ViewLegalEntityPage/licenceRequirementsHelper';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getGalleryMain } from 'requests/gallery';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { ActionTypes } from 'state/actions/alert';
import { colors } from 'theme/colors';
import { StyledPageTitle } from 'theme/styles';
import { formatDate } from 'utils/date';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { isPathEqualToRouteName } from 'utils/route';
import { Elabels, getBreadcrumbsBasic, renderPaymentStatusFieldColor } from '../helpers';
import { useCase } from '../hooks';
import { StyledDetail } from '../styles';
import AddDecisionForm from './AddDecisionForm';
import AssignCaseForm from './AssignCaseForm';
import Attachments from './Attachments';
import CloseTaskForm from './CloseTaskForm';
import Comments from './Comments';
import History from './History';
import Licences from './Licences';
import RejectCaseForm from './RejectCaseForm';
import Request from './Request';
import CaseUpdatePaymentForm from './UpdatePayment';
import VerificationProcess from './VerificationProcess';

export enum ETabDetailsId {
  licences = 'licences',
}

const CaseDetailsPage = () => {
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const query = useQuery();
  const expand: string | null = query.get('expand');
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityCasesDetailsPage);
  const [defaultTab, setDefaultTab] = useState(0);
  const [detailsTabs, setDetailsTabs] = useState<Tab[]>([]);
  const [isLoadingCaseData, setIsLoadingCaseData] = useState(true);
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { currency, shortDateFormat, getProperties } = useGlobalProperty();
  const { user, getUser } = useAuth();
  const { clearAlert, samePageAlert, nextPageAlert, setAlert } = useAlert();
  const [type, setType] = useState<ELegalEntityTypes>();
  const { canAssignCase } = usePrivilegesValidation();
  const {
    getCaseData,
    caseData,
    handleVerifyCase,
    handleFinishVerificationCase,
    handleCloseCase,
  } = useCase();
  const { selectedLanguage } = useLang();
  const {
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    weaponLicenceCaseStatus,
    getWeaponLicenceCaseStatus,
    caseProcessTypes,
    getCaseProcessTypes,
    weaponLicencePaymentStatus,
    getWeaponLicencePaymentStatus,
  } = useDictionary();

  useEffect(() => {
    getProperties();
    getUser();
  }, []);

  useEffect(() => {
    if (caseData) {
      setType(
        caseData.legalType === LETypes.individual
          ? ELegalEntityTypes.individual
          : ELegalEntityTypes.organisation,
      );
    }
  }, [caseData]);

  useEffect(() => {
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    if (!weaponLicenceCaseStatus) {
      getWeaponLicenceCaseStatus();
    }
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    if (!weaponLicencePaymentStatus) {
      getWeaponLicencePaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const [canAddLicence, setCanAddLicence] = useState<boolean | null>(false);
  const [modalAssignCase, setModalAssignCase] = useState<boolean>(false);
  const [modalAddDecision, setModalAddDecision] = useState<boolean>(false);
  const [modalVerifyCase, setModalVerifyCase] = useState<boolean>(false);
  const [modalFinishVerification, setModalFinishVerification] = useState<boolean>(false);
  const [modalCloseCase, setModalCloseCase] = useState<boolean>(false);
  const [modalCloseTask, setModalCloseTask] = useState<boolean>(false);
  const [modalRejectCase, setModalRejectCase] = useState<boolean>(false);
  const [modalUpdatePayment, setModalUpdatePayment] = useState<boolean>(false);
  const [currentMissingFields, setCurrentMissingFields] = useState<boolean>();
  const [currentMissingFieldsLoading, setCurrentMissingFieldsLoading] = useState(true);
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>(null);
  const [legalEntityPhoto, setLegalEntityPhoto] = useState(false);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>(null);
  // const [selectedSortType, setSelectedSortType] = useState<ECaseSortType>(ECaseSortType.Cases);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [missingFieldsModalOpen, setMissingFieldsModalOpen] = useState(false);
  const [isOtherRequest, setIsOtherRequest] = useState<boolean>();
  const [isApplicationRequest, setIsApplicationRequest] = useState<boolean>();
  const [isRenewalRequest, setIsRenewalRequest] = useState<boolean>();
  const [isVariationRequest, setIsVariationRequest] = useState<boolean>();
  const { handleReprintLicence } = useCase();
  const [isSubmitLoading, setSubmitLoading] = useState(false);

  const onClickReprintLicence = async () => {
    if (isSubmitLoading) return false;

    setSubmitLoading(true);
    const res = await handleReprintLicence(caseId);
    if (res) {
      updateData();
    }
    setSubmitLoading(false);
  };

  useEffect(() => {
    (async () => {
      setIsLoadingCaseData(true);
      await getCaseData(caseId);
      setIsLoadingCaseData(false);
    })();
  }, [caseId]);

  useEffect(() => {
    if (caseData) {
      setIsOtherRequest(caseData.processTypeUniqueId === ECaseProcessTypes.OTHER_REQUEST);
      setIsApplicationRequest(caseData.processTypeUniqueId === ECaseProcessTypes.APPLICATION);
      setIsRenewalRequest(caseData.processTypeUniqueId === ECaseProcessTypes.RENEWAL);
      setIsVariationRequest(caseData.processTypeUniqueId === ECaseProcessTypes.VARIATION);
    }
  }, [caseData]);

  const updateData = async () => {
    await getCaseData(caseId);
  };

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (caseData && isOtherRequest !== undefined) {
      const caseStatus = caseData.statusUniqueId as CaseStatus;
      const isCaseDisabled = FINAL_CASE_STATUSES.includes(caseStatus);
      const canAdd =
        hasPermissions([EPermission.CREATE_MANAGE_LICENCE]) &&
        ((CaseStatus.GRANTED === caseStatus && caseData.canCreateLicence) ||
          CaseStatus.CLOSED === caseStatus);
      setCanAddLicence(canAdd);

      const isPendingVerificationAllocation = [
        CaseStatus.PENDING_ALLOCATION,
        CaseStatus.PENDING_VERIFICATION,
      ].includes(caseStatus);

      const hideVerificationProcess =
        isPendingVerificationAllocation &&
        caseData.processTypeUniqueId === ECaseProcessTypes.VARIATION;

      const tabs = [
        ...(!caseData.isUnderVerification && !isOtherRequest && !hideVerificationProcess
          ? [
              {
                title: Elabels.application,
                content: (
                  <VerificationProcess
                    isUnderVerification={false}
                    statusUniqueId={caseData.statusUniqueId}
                    assignedToUniqueId={caseData.assignedToUniqueId}
                    isFromLE={isFromLE}
                    summaryReportGenerated={caseData.summaryReportGenerated}
                    updateCase={updateData}
                    summaryReportName={caseData.summaryReportFileName}
                    processTypeUniqueId={caseData.processTypeUniqueId}
                    legalEntityId={caseData.legalUniqueId}
                  />
                ),
              },
            ]
          : []),
        ...(caseData.isUnderVerification && !isOtherRequest && !hideVerificationProcess
          ? [
              {
                title: Elabels.verificationProcess,
                content: (
                  <VerificationProcess
                    isUnderVerification
                    isFromLE={isFromLE}
                    statusUniqueId={caseData.statusUniqueId}
                    assignedToUniqueId={caseData.assignedToUniqueId}
                    summaryReportGenerated={caseData.summaryReportGenerated}
                    updateCase={updateData}
                    summaryReportName={caseData.summaryReportFileName}
                    processTypeUniqueId={caseData.processTypeUniqueId}
                    legalEntityId={caseData.legalUniqueId}
                  />
                ),
              },
            ]
          : []),
        ...(!isOtherRequest
          ? [
              {
                title: Elabels.comments,
                content: (
                  <Comments
                    isReadOnly={caseData.statusUniqueId !== CaseStatus.IN_PROGRESS}
                    isAssignUser={
                      caseData.assignedToUniqueId === user?.userId &&
                      !!hasPermissions([EPermission.VERIFY_CASES])
                    }
                    isSupervisor={!!hasPermissions([EPermission.ADD_DECISION])}
                  />
                ),
              },
            ]
          : []),
        ...(!isOtherRequest
          ? [
              {
                title: Elabels.attachments,
                content: (
                  <Attachments
                    isReadOnly={isCaseDisabled}
                    isSupervisor={!!hasPermissions([EPermission.ADD_DECISION])}
                    isAssignUser={
                      caseData.assignedToUniqueId === user?.userId &&
                      !!hasPermissions([EPermission.VERIFY_CASES])
                    }
                  />
                ),
              },
            ]
          : []),
        ...(isOtherRequest
          ? [
              {
                title: Elabels.request,
                content: (
                  <Request
                    caseData={caseData}
                    isAssignUser={caseData.assignedToUniqueId === user?.userId}
                    isClosed={caseData.statusUniqueId === CaseStatus.CLOSED}
                    hasPermissionsOrOtherTask={!!hasPermissionsOrOtherTask}
                  />
                ),
              },
            ]
          : []),
        {
          title: Elabels.history,
          content: <History />,
        },
        ...(canAdd && isApplicationRequest
          ? [
              {
                name: ETabDetailsId.licences,
                title: Elabels.licences,
                content: <Licences />,
              },
            ]
          : []),
      ];
      setDetailsTabs(tabs);
    }
  }, [caseData, isOtherRequest]);

  const onClickVerifyAssignCase = async () => {
    await handleVerifyCase(caseId);
    setModalVerifyCase(false);
    updateData();
  };

  const onClickFinishVerificationCase = async () => {
    const result = await handleFinishVerificationCase(caseId);
    setModalFinishVerification(false);
    if (result) {
      updateData();
    }
  };

  const onClickCloseCase = async () => {
    const result = await handleCloseCase(caseId);
    setModalCloseCase(false);
    if (result) {
      await updateData();
    }
  };

  const breadcrumbs = getBreadcrumbsBasic(isFromLE, type ?? null, t, !!isOtherRequest, id);

  const { getLegalEntityAddressesData, legalEntityAddress } = useLegalEntities();
  useEffect(() => {
    if (caseData && canAddLicence && (legalEntity || orgLegalEntity)) {
      const fields =
        caseData.legalType === LETypes.individual
          ? getMissingFieldsForIndividual(legalEntity, legalEntityAddress, legalEntityPhoto)
          : getMissingFieldsForOrganisation(orgLegalEntity, legalEntityAddress);
      setMissingFields(fields);
      setCurrentMissingFields(!!fields.length);
      setCurrentMissingFieldsLoading(false);
    }
  }, [
    caseData,
    legalEntity,
    orgLegalEntity,
    canAddLicence,
    legalEntityAddress,
    legalEntityPhoto,
  ]);

  useEffect(() => {
    (async function getLegalEntityAddressInit() {
      if (canAddLicence && caseData && caseData.legalUniqueId) {
        await getLegalEntityAddressesData(caseData?.legalUniqueId);

        if (caseData.legalType === LETypes.individual) {
          (async function getLegalEntityDataInit() {
            if (caseData.legalUniqueId) {
              const response = await getLegalEntity(caseData.legalUniqueId);
              if (response) {
                const photoData = await getGalleryMain(response.uniqueId);
                if (photoData) {
                  setLegalEntityPhoto(true);
                }
                setLegalEntity(response);
              }
            }
          })();
          setOrgLegalEntity(null);
        } else {
          (async function getLegalEntityDataInit() {
            if (caseData.legalUniqueId) {
              const response = await getOrgLegalEntity(caseData.legalUniqueId);
              if (response) {
                setOrgLegalEntity(response);
              }
            }
          })();
          setLegalEntity(null);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, canAddLicence]);

  useEffect(() => {
    if (expand && detailsTabs.length) {
      const tabIndex = detailsTabs.findIndex((tab) => tab.name === expand);
      if (tabIndex >= 0) {
        setDefaultTab(tabIndex);
      } else {
        setDefaultTab(0);
      }
    }
  }, [expand, detailsTabs]);

  const handleAddLicenceButtonClick = () => {
    if (caseData?.paymentStatusUniqueId !== EPaymentStatusId.PAID) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('caseManagement.cases.paymentRequired.text'),
        variant: EAlertVariants.error,
      });
      return;
    }
    if (!currentMissingFields) {
      navigate(
        isFromLE
          ? `${ERouteLinks.LegalEntityCaseAddLicencePage.replace(':id', id).replace(':caseId', caseId)}?type=${type}`
          : `${ERouteLinks.CaseAddLicencePage.replace(':caseId', caseId)}`,
      );
    } else {
      setMissingFieldsModalOpen(true);
    }
  };
  const handleVaryLicenceButtonClick = () => {
    if (caseData?.paymentStatusUniqueId !== EPaymentStatusId.PAID) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('caseManagement.cases.paymentRequired.text'),
        variant: EAlertVariants.error,
      });
      return;
    }
    if (!currentMissingFields && caseData?.weaponLicenceUniqueId) {
      navigate(
        isFromLE
          ? `${ERouteLinks.LegalEntityCreateLicenceVaryCase.replace(':id', id).replace(':licenceId', caseData.weaponLicenceUniqueId).replace(':caseId', caseId)}?type=${type}`
          : `${ERouteLinks.CaseCreateLicenceVary.replace(':caseId', caseId).replace(':licenceId', caseData.weaponLicenceUniqueId)}?type=${type}`,
      );
    } else {
      setMissingFieldsModalOpen(true);
    }
  };
  const handleRenewLicenceButtonClick = () => {
    if (caseData?.paymentStatusUniqueId !== EPaymentStatusId.PAID) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('caseManagement.cases.paymentRequired.text'),
        variant: EAlertVariants.error,
      });
      return;
    }
    if (!currentMissingFields && caseData?.weaponLicenceUniqueId) {
      navigate(
        isFromLE
          ? `${ERouteLinks.LegalEntityCreateLicenceRenewCase.replace(':id', id).replace(':licenceId', caseData.weaponLicenceUniqueId).replace(':caseId', caseId)}?type=${type}`
          : `${ERouteLinks.CaseCreateLicenceRenew.replace(':caseId', caseId).replace(':licenceId', caseData.weaponLicenceUniqueId)}?type=${type}`,
      );
    } else {
      setMissingFieldsModalOpen(true);
    }
  };

  const canAddDecision =
    !!caseData &&
    (isApplicationRequest || isRenewalRequest || isVariationRequest) &&
    !!hasPermissions([EPermission.ADD_DECISION]) &&
    caseData.assignedToUniqueId === user?.userId &&
    [CaseStatus.WAITING_FOR_DECISION, CaseStatus.SUPERVISOR_REVIEW].includes(
      caseData.statusUniqueId as CaseStatus,
    );

  const canFinishVerification =
    !!caseData &&
    (isApplicationRequest || isRenewalRequest || isVariationRequest) &&
    !!hasPermissions([EPermission.VERIFY_CASES]) &&
    caseData.assignedToUniqueId === user?.userId &&
    [CaseStatus.IN_PROGRESS].includes(caseData.statusUniqueId as CaseStatus);

  const canRejectCase =
    !!caseData &&
    (isApplicationRequest || isRenewalRequest || isVariationRequest) &&
    !!hasPermissions([EPermission.ADD_CASES, EPermission.ADD_DECISION], true) &&
    caseData.assignedToUniqueId === user?.userId &&
    !FINAL_CASE_STATUSES.includes(caseData.statusUniqueId as CaseStatus);

  const canReprintLicence =
    !!hasPermissions(EPermission.PRINT_LICENCE) &&
    !!caseData &&
    caseData.taskNameUniqueId === ECaseTaskNames.REPRINT_LICENCE &&
    REPRINT_LICENCE_CASE_STATUSES.includes(caseData.statusUniqueId as CaseStatus) &&
    !caseData.reprintEnqueued &&
    !caseData.reprintSent;

  const hasPermissionsOrOtherTask =
    caseData &&
    (caseData.taskNameUniqueId !== ECaseTaskNames.REPRINT_LICENCE ||
      (caseData.taskNameUniqueId === ECaseTaskNames.REPRINT_LICENCE &&
        !!hasPermissions(EPermission.PRINT_LICENCE)));

  const canAssignTo =
    caseData && canAssignCase(caseData.licenceAuthorityUniqueId, caseData.statusUniqueId);

  let reprintAlert: { label: Elabels; variant: EAlertVariants } | null = null;
  if (caseData?.reprintSent) {
    reprintAlert = { label: Elabels.reprintSent, variant: EAlertVariants.success };
  } else if (caseData?.reprintEnqueued) {
    reprintAlert = { label: Elabels.reprintEnqueued, variant: EAlertVariants.warning };
  }

  return (
    <>
      <Breadcrumbs items={[...breadcrumbs, t(Elabels.caseDetailsHeader)]} />
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          columnGap: '20px',
          rowGap: 1,
          mb: '20px',
        }}
      >
        <Grid sx={{ alignItems: 'center' }}>
          <StyledPageTitle variant="h4">{t(Elabels.caseDetailsHeader)}</StyledPageTitle>
        </Grid>
        {isLoadingCaseData && (
          <Grid size={{ xs: 12 }}>
            <Loader isLoading />
          </Grid>
        )}
        <FormModal
          open={missingFieldsModalOpen}
          onClickClose={() => setMissingFieldsModalOpen(false)}
          cancelButtonLabel={t('general.close.label')}
        >
          <Typography variant="h5" sx={{ color: colors.primary500 }}>
            {t('legalEntity.missingData.header')}
          </Typography>
          <Stack sx={{ gap: '24px' }}>
            <Typography variant="body2">{t('legalEntity.missingData.body1')}</Typography>
            <Typography variant="body2">
              <ul style={{ listStyleType: 'initial', marginLeft: '24px' }}>
                {missingFields.map((field, index) => (
                  <li key={index}>{t(field)}</li>
                ))}
              </ul>
            </Typography>
            <Typography variant="body2">{t('legalEntity.missingData.body2')}</Typography>
          </Stack>
        </FormModal>
        {!isLoadingCaseData && caseData && (
          <Grid
            container
            size={{ xs: 12, md: 'auto' }}
            sx={{ gap: 1, justifyContent: 'flex-end', ml: { xs: 0, md: 'auto' } }}
          >
            {canReprintLicence && (
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Button
                  label={t(Elabels.reprintLicence)}
                  variant={EButtonVariants.contained}
                  onClick={onClickReprintLicence}
                  size={EButtonSizes.small}
                  fullWidth
                  id="case_details_page_update_reprint-licence-button"
                />
              </Grid>
            )}
            {REVIEW_APPLICATION_CASE_STATUSES.includes(
              caseData.statusUniqueId as CaseStatus,
            ) &&
              (isApplicationRequest || isRenewalRequest || isVariationRequest) &&
              caseData.assignedToUniqueId === user?.userId && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.updatePayment)}
                    variant={EButtonVariants.contained}
                    onClick={() => setModalUpdatePayment(true)}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_update_payment-button"
                    permission={[EPermission.VERIFY_CASES]}
                  />
                </Grid>
              )}
            {canAddDecision && (
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Button
                  label={t(Elabels.addDecision)}
                  variant={EButtonVariants.contained}
                  onClick={() => setModalAddDecision(true)}
                  size={EButtonSizes.small}
                  fullWidth
                  id="case_details_page_add-button"
                />
              </Grid>
            )}
            {canFinishVerification && (
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Button
                  label={t(Elabels.finishVerification)}
                  variant={EButtonVariants.contained}
                  onClick={() => setModalFinishVerification(true)}
                  size={EButtonSizes.small}
                  fullWidth
                  id="case_details_page_finish_verification-button"
                />
              </Grid>
            )}
            {canRejectCase && (
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Button
                  label={t(Elabels.rejectCase)}
                  variant={EButtonVariants.contained}
                  onClick={() => setModalRejectCase(true)}
                  size={EButtonSizes.small}
                  fullWidth
                  id="case_details_page_reject-button"
                />
              </Grid>
            )}
            {caseData.statusUniqueId === CaseStatus.IN_PROGRESS &&
              hasPermissions([EPermission.VERIFY_CASES]) &&
              caseData?.assignedToUniqueId === user?.userId &&
              (isApplicationRequest || isRenewalRequest || isVariationRequest) &&
              !caseData.isUnderVerification && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.verifyTheCase)}
                    variant={EButtonVariants.contained}
                    onClick={() => setModalVerifyCase(true)}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_verify-button"
                  />
                </Grid>
              )}
            {caseData.statusUniqueId === CaseStatus.GRANTED &&
              (isApplicationRequest || isRenewalRequest || isVariationRequest) && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.closeCase)}
                    variant={EButtonVariants.contained}
                    onClick={() => setModalCloseCase(true)}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_close-button"
                    permission={[EPermission.ADD_CASES]}
                  />
                </Grid>
              )}
            {caseData.statusUniqueId === CaseStatus.IN_PROGRESS &&
              isOtherRequest &&
              hasPermissionsOrOtherTask &&
              caseData?.assignedToUniqueId === user?.userId && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.closeTask)}
                    variant={EButtonVariants.contained}
                    onClick={() => setModalCloseTask(true)}
                    size={EButtonSizes.small}
                    fullWidth
                    permission={EPermission.VERIFY_CASES}
                    id="case_details_page_close-button"
                  />
                </Grid>
              )}
            {canAssignTo && (
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Button
                  label={t(Elabels.assignTo)}
                  variant={EButtonVariants.contained}
                  onClick={() => setModalAssignCase(true)}
                  size={EButtonSizes.small}
                  id="case_details_page_assign-button"
                  fullWidth
                />
              </Grid>
            )}

            {canAddLicence &&
              caseData.canCreateLicence &&
              !currentMissingFieldsLoading &&
              ECaseProcessTypes.APPLICATION === caseData?.processTypeUniqueId && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.createLicence)}
                    variant={EButtonVariants.contained}
                    onClick={handleAddLicenceButtonClick}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_create_licence-button"
                  />
                </Grid>
              )}
            {canAddLicence &&
              caseData.canCreateLicence &&
              !currentMissingFieldsLoading &&
              caseData?.canVary &&
              ECaseProcessTypes.VARIATION === caseData?.processTypeUniqueId && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.varyLicence)}
                    variant={EButtonVariants.contained}
                    onClick={handleVaryLicenceButtonClick}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_create_vary_licence-button"
                  />
                </Grid>
              )}
            {canAddLicence &&
              caseData.canCreateLicence &&
              !currentMissingFieldsLoading &&
              caseData?.canRenew &&
              ECaseProcessTypes.RENEWAL === caseData?.processTypeUniqueId && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.renewLicence)}
                    variant={EButtonVariants.contained}
                    onClick={handleRenewLicenceButtonClick}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_create_renew_licence-button"
                  />
                </Grid>
              )}

            {caseData.statusUniqueId === CaseStatus.PENDING_VERIFICATION &&
              hasPermissions([EPermission.VERIFY_CASES]) &&
              caseData?.assignedToUniqueId === user?.userId &&
              !caseData.isUnderVerification && (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    label={t(Elabels.verifyTheCase)}
                    variant={EButtonVariants.contained}
                    onClick={() => setModalVerifyCase(true)}
                    size={EButtonSizes.small}
                    fullWidth
                    id="case_details_page_verify2-button"
                  />
                </Grid>
              )}
          </Grid>
        )}
      </Grid>

      <Grid sx={{ alignItems: 'center' }}>
        {nextPageAlert && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
          </Box>
        )}
      </Grid>

      <Grid sx={{ alignItems: 'center' }}>
        {samePageAlert && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        )}
      </Grid>

      <Grid sx={{ alignItems: 'center' }}>
        {reprintAlert && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Alert text={t(reprintAlert.label)} variant={reprintAlert.variant} />
          </Box>
        )}
      </Grid>

      <CaseUpdatePaymentForm
        caseId={caseId}
        paymentId={caseData?.paymentStatusUniqueId}
        isOpen={modalUpdatePayment}
        close={() => {
          setModalUpdatePayment(false);
          updateData();
        }}
      />

      <FormModal
        open={modalVerifyCase}
        onClickClose={() => setModalVerifyCase(false)}
        onClickApprove={onClickVerifyAssignCase}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.verifyCase.label')}
        titleLeft
        text={t('caseManagement.cases.areYouSureVerify.text')}
      />
      {canFinishVerification && (
        <FormModal
          open={modalFinishVerification}
          onClickClose={() => setModalFinishVerification(false)}
          onClickApprove={onClickFinishVerificationCase}
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
          title={t(Elabels.finishVerification)}
          titleLeft
          text={t('caseManagement.cases.areYouSureFinishVerification.text')}
        />
      )}
      <FormModal
        open={modalCloseCase}
        onClickClose={() => setModalCloseCase(false)}
        onClickApprove={onClickCloseCase}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t(Elabels.closeCase)}
        titleLeft
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {t('caseManagement.cases.closeCaseInfo.text')
            .split(';')
            .map((sentence, index) => (
              <Typography variant="body2" key={index} sx={{ mt: index === 2 ? 2 : 0 }}>
                {sentence}
              </Typography>
            ))}
        </Box>
      </FormModal>

      {caseData && canAssignTo && (
        <AssignCaseForm
          isOpen={modalAssignCase}
          close={() => setModalAssignCase(false)}
          updateData={updateData}
          caseId={caseId}
          currentWorkerId={caseData?.assignedToUniqueId}
        />
      )}

      {canAddDecision && (
        <AddDecisionForm
          isOpen={modalAddDecision}
          close={() => setModalAddDecision(false)}
          caseId={caseId}
          updateData={updateData}
        />
      )}

      {canRejectCase && (
        <RejectCaseForm
          isOpen={modalRejectCase}
          close={() => setModalRejectCase(false)}
          caseId={caseId}
          updateData={updateData}
        />
      )}
      {caseData?.statusUniqueId === CaseStatus.IN_PROGRESS && isOtherRequest && (
        <CloseTaskForm
          isOpen={modalCloseTask}
          close={() => setModalCloseTask(false)}
          caseId={caseId}
          updateData={updateData}
        />
      )}

      {caseData && !isLoadingCaseData && (
        <StyledDetail container size={{ xs: 12 }}>
          {isOtherRequest ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.taskName)}
                </Typography>
                <Typography variant="body2">{emptyFieldHolder(caseData.taskName)}</Typography>
              </Stack>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.licenceCodeName)}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(caseData.licenceTemplateCode)}{' '}
                  {emptyFieldHolder(caseData.licenceTemplateName)}
                </Typography>
              </Stack>
            </Grid>
          )}
          {caseData.reprintSent && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.reprintedLicence)}
                </Typography>
                <Typography variant="body2">
                  {caseData.reprintedLicenceUniqueId ? (
                    <Link
                      href={`${ERouteLinks.LegalEntityViewLicence.replace(
                        ':id',
                        caseData.legalUniqueId,
                      ).replace(
                        ':licenceId',
                        caseData.reprintedLicenceUniqueId,
                      )}?type=${type}`}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {emptyFieldHolder(caseData.reprintedLicenceName)},{' '}
                      {caseData.reprintedLicenceNumber}
                    </Link>
                  ) : (
                    `${emptyFieldHolder(caseData.reprintedLicenceName)}, ${
                      caseData.reprintedLicenceNumber
                    }`
                  )}
                </Typography>
              </Stack>
            </Grid>
          )}
          {FINAL_CASE_STATUSES.includes(caseData.statusUniqueId as CaseStatus) ? (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.decisionDate)}
                </Typography>
                <Typography variant="body2">
                  <CountdownTimer
                    date={caseData.createdDate}
                    endDate={caseData.decisionDate}
                  />
                </Typography>
              </Stack>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.timeSinceCreation)}
                </Typography>
                <Typography variant="body2">
                  <CountdownTimer date={caseData.createdDate} />
                </Typography>
              </Stack>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.status)}
              </Typography>
              <Typography variant="body2">
                <Chips
                  text={findInDictionary(caseData.statusUniqueId, weaponLicenceCaseStatus)}
                  variant={renderStatusCaseFieldColor(caseData)}
                ></Chips>
              </Typography>
            </Stack>
          </Grid>
          {(isRenewalRequest || isVariationRequest) && (
            <Grid size={{ xs: 12 }} container>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.licenceNumber)}
                  </Typography>
                  {caseData.weaponLicenceUniqueId ? (
                    <Typography variant="body2">
                      <Link
                        href={`${ERouteLinks.DetailLicence.replace(':licenceId', caseData.weaponLicenceUniqueId)}`}
                        underline="none"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {caseData.weaponLicenceNumber}
                      </Link>
                    </Typography>
                  ) : (
                    <Typography variant="body2">{caseData.weaponLicenceNumber}</Typography>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.startEndDate)}
                  </Typography>
                  <Typography variant="body2">
                    {`${formatDate(caseData.weaponLicenceStartDate, shortDateFormat)} - ${formatDate(caseData.weaponLicenceEndDate, shortDateFormat)}`}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.legalEntityName)}
              </Typography>
              <Typography variant="body2">
                <Link
                  href={`${ERouteLinks.ViewLegalEntity.replace(':id', caseData.legalUniqueId)}?type=${type}`}
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {caseData.legalName}
                </Link>
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.legalEntityType)}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  caseData.legalType === LETypes.individual
                    ? ELegalEntityTypesName.individual
                    : ELegalEntityTypesName.organisation,
                )}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.licenceAuthority)}
              </Typography>
              <Typography variant="body2">
                {findInDictionary(caseData.licenceAuthorityUniqueId, weaponLicencingAuthority)}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.caseID)}
              </Typography>
              <Typography variant="body2">{emptyFieldHolder(caseData.caseNumber)}</Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.processType)}
              </Typography>
              <Typography variant="body2">
                {findInDictionary(caseData.processTypeUniqueId, caseProcessTypes)}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(Elabels.assignedResource)}
              </Typography>
              <Typography variant="body2" id="case_details_case_assigned-resource-text">
                {caseData.assignedTo ? (
                  caseData.assignedTo
                ) : (
                  <Typography
                    variant="body2"
                    color={colors.gray400}
                    style={{ fontStyle: 'italic' }}
                  >
                    {t(Elabels.notAssigned)}
                  </Typography>
                )}
              </Typography>
            </Stack>
          </Grid>
          {!isOtherRequest && (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.paymentStatus)}
                </Typography>
                <Typography variant="body2">
                  <Chips
                    text={`${findInDictionary(caseData.paymentStatusUniqueId, weaponLicencePaymentStatus)} - ${caseData.price ? `${caseData.price} ${currency}` : ''}`}
                    variant={renderPaymentStatusFieldColor(caseData)}
                  />
                </Typography>
              </Stack>
            </Grid>
          )}
        </StyledDetail>
      )}
      {!isLoadingCaseData && (
        <Grid sx={{ gap: 3 }}>
          <Grid size={{ xs: 12 }}>
            <TabMenu tabs={detailsTabs} defaultTab={defaultTab} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CaseDetailsPage;
